import { createSignal, onCleanup, onMount, Show } from 'solid-js';
import { ButtonColorName } from '../../style/color-classes';
import { ButtonProps } from '../../ui-components/button/button.types';
import { A } from '@solidjs/router';
import clsx from 'clsx';
import { RenderChildren } from './children/render-children';
import { useAppContext } from '../../app-context-provider/app-context-provider';

export type PricingProps = {
    variant: string;
    backgroundColor?: {
        name: string;
        color: string;
    };
    alignLists: boolean;
    heading: string;
    headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    price: string;
    description: string;
    listItems: number;
    values: {
        boldText: string;
        normalText: string;
    }[];
    button: Partial<ButtonProps>;
    hash: string;
};

export const Pricing = (props: PricingProps) => {
    const { translate } = useAppContext();
    const [isShowing, setIsShowing] = createSignal(false);
    let observer: IntersectionObserver;

    const { values, variant, button, listItems, backgroundColor, hash, headingTag = 'h3' } = props;

    const safeButton: ButtonProps = {
        arrow: button.arrow || false,
        buttonSize: button.buttonSize || 'small',
        color: button.color || ButtonColorName.Primary,
        darkMode: button.darkMode || false,
        fullWidth: button.fullWidth || true,
        label: button.label || translate('read-more'),
        target: button.target || '_blank',
        url: undefined,
        variant: button.variant || 'solid',
    };

    const filteredValues = () =>
        values.filter((value, index) => {
            return (value.boldText || value.normalText) && index < listItems;
        });

    const hasLink = () => props.button?.url || null;
    const backgroundColorClass = () => (backgroundColor ? `bg-${backgroundColor?.name}` : '');

    const classList = clsx(
        { 'bg-white': variant === 'solid' },
        { 'bg-accent1-100 border border-black rounded-l': variant === 'outline' },
        { [backgroundColorClass()]: variant === 'outline' && !!backgroundColor },
        { 'hover:scale-105': !!hasLink() },
        { 'flex flex-col': !props.alignLists },
        { 'flex flex-col': props.alignLists },
    );

    onMount(() => {
        const el = document.getElementById(hash);
        if (!el) {
            return;
        }

        if (!observer) {
            observer = new IntersectionObserver(([entry]) => {
                if (entry.target.id !== hash) {
                    return;
                }

                setIsShowing(entry.isIntersecting);
            });
        }

        observer.observe(el);
    });

    onCleanup(() => observer.disconnect());

    return (
        <Show
            when={props.button.url}
            fallback={
                <div
                    class="h-full transition-all delay-100 duration-1000 ease-in-out"
                    classList={{
                        'opacity-0 translate-y-10': !isShowing(),
                        'opacity-100': isShowing(),
                    }}
                    id={hash}
                >
                    <RenderChildren
                        classList={classList}
                        hasLink={!!hasLink()}
                        props={{
                            ...props,
                            button: { ...safeButton },
                            values: filteredValues(),
                            headingTag,
                        }}
                    />
                </div>
            }
        >
            <A
                href={props.button.url || ''}
                class="h-full transition-all delay-100 duration-1000 ease-in-out"
                classList={{
                    'opacity-0 translate-y-10': !isShowing(),
                    'opacity-100': isShowing(),
                }}
                target={props.button.target}
                rel={props.button.rel}
                id={hash}
            >
                {
                    <RenderChildren
                        classList={classList}
                        hasLink={!!hasLink()}
                        props={{
                            ...props,
                            button: { ...safeButton },
                            values: filteredValues(),
                            headingTag,
                        }}
                    />
                }
            </A>
        </Show>
    );
};

Pricing.parseProps = (atts: PricingProps) => {
    const { variant, backgroundColor, alignLists, heading, headingTag = 'h3', price, listItems, values, button, description, hash } = atts;
    const unwrappedButton = button ? JSON.parse(JSON.stringify(button)) : {};
    const unwrappedbackgroundColor = backgroundColor ? JSON.parse(JSON.stringify(backgroundColor)) : {};

    const commonAtss = {
        variant,
        backgroundColor: unwrappedbackgroundColor,
        alignLists,
        heading,
        headingTag,
        price,
        description,
        listItems,
        values,
        button: unwrappedButton,
        hash,
    };

    return commonAtss;
};
